import lang from "dojo/_base/lang";
import csi from "csi/main";
import { C8Node } from "csi/typings";

interface LocaleType {
	Name?: string;
	Plural?: string;
	Tooltip?: string;
	Action?: Record<string, string>;
	Attr?: Record<string, string>;
	Path?: Record<string, string>;
	View?: Record<string, string>;
}

type LocaleView = string | Record<string, string | LocaleViewBag>;
interface LocaleViewBag extends Record<string, string | LocaleViewBag> { }

interface ResourceNode extends C8Node {
	Resources: Record<string, string>;
	Types: Record<string, string>;
	Views: Record<string, string>;
};

function _initFromMap(map: Record<string, string>, scope: Record<string, LocaleType | LocaleView>): void {
	for (const [key, value] of Object.entries(map)) {
		const n = key.lastIndexOf(".");
		const id = key.substr(n + 1);
		const newkey = key.substr(0, n);

		if (id && newkey) {
			const o = lang.getObject(newkey, true, scope);
			if (o) {
				o[id] = value;
			}
		}
	}
}

class LocaleResources {
	// String: locale of stored resources
	readonly locale: string;

	// string-string mapping for UI text resources
	resources: Record<string, string> = {};

	// class mappings for type names, attributes, paths and actions
	types: Record<string, LocaleType> = {};

	// view resource names
	views: Record<string, LocaleView> = {};

	constructor(locale: string) {
		this.locale = locale;
	}

	// mixin new resources
	addResources(obj: Record<string, string>): void {
		Object.assign(this.resources, obj);
	}

	// deep mixin of new type resources
	addTypes(obj: Record<string, LocaleType>): void {
		csi.mergeObject(obj, this.types, true);
	}

	// deep mixin of new view resources
	addViews(obj: Record<string, LocaleView>): void {
		csi.mergeObject(obj, this.views, true);
	}

	// initialize resources from LocaleConfiguration node
	initFromNode(node: ResourceNode): void {
		console.assert(this.locale == node.$Name);

		this.resources = node.Resources;
		_initFromMap(node.Types, this.types);
		_initFromMap(node.Views, this.views);
	}

	// set resource on given object from localeResources (resource, types, views) with given value
	setResource(obj: Record<string, any>, keys: string[], value: string): void {
		const key = keys.pop();

		if (keys.length) {
			obj = lang.getObject(keys.join("."), true, obj);
		}

		obj[key] = value;
	}

	// convert resource to displayable string
	resource(idRes: string): string | undefined {
		return this.resources[idRes];
	}

	// convert node type to name for type - types cannot be parameterized
	typeName(idType: string): string | null {
		const o = this.types[idType];
		return o ? o.Name : null;
	}

	// return mapping for id under given type object
	typeResource<K extends keyof LocaleType>(
		idType: string,
		id: K,
	): LocaleType[K] | null {
		const o = this.types[idType];
		return o ? o[id] : null;
	}

	// return mapping for id under given type object
	typeObjectResource<K extends keyof LocaleType>(
		idType: string,
		idObject: K,
		id: string,
	): string | null {
		const o = this.types[idType];
		const k = o ? o[idObject] : null;
		return k ? k[id] : null;
	}

	view(idType: string, idView: string): LocaleView | null {
		// summary: access view object by type and view
		const o = idType ? this.views[idType] : null;
		return o && idView ? o[idView] : null;
	}
}

export = LocaleResources;
